import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  AiChatTableDialogComponent,
  AlloTableDialogData,
} from '../components/messages/ai-chat-table-dialog/ai-chat-table-dialog.component';
import { exportTableElementAsCsv } from './allo-table-csv';

@Injectable({
  providedIn: 'root',
})
export class AiChatActionHandlerService {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  onMessageContentClick(event: MouseEvent): void {
    if (this.shouldIgnoreClick(event)) {
      return;
    }

    const target = event.target as Element | null;
    if (!target) {
      return;
    }

    if (this.tryHandleExportClick(target, event)) {
      return;
    }

    if (this.tryHandleExpandClick(target, event)) {
      return;
    }

    if (!this.canHandleNavigationClick(event)) {
      return;
    }

    this.tryHandleActionLinkNavigation(target, event);
  }

  private shouldIgnoreClick(event: MouseEvent): boolean {
    return event.defaultPrevented;
  }

  private tryHandleExportClick(target: Element, event: MouseEvent): boolean {
    const exportButton = target.closest(
      'button.allo-export, button.qt-table-export-btn'
    ) as HTMLButtonElement | null;
    if (!exportButton) {
      return false;
    }

    event.preventDefault();
    this.exportTableAsCsv(exportButton);
    return true;
  }

  private canHandleNavigationClick(event: MouseEvent): boolean {
    return !(
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    );
  }

  private tryHandleActionLinkNavigation(
    target: Element,
    event: MouseEvent
  ): boolean {
    const actionLink = target.closest(
      'a.qt-action-link'
    ) as HTMLAnchorElement | null;
    const targetUrl = this.getInternalActionLinkTarget(actionLink);

    if (!targetUrl) {
      return false;
    }

    event.preventDefault();
    void this.navigateToUrl(targetUrl);
    return true;
  }

  private getInternalActionLinkTarget(
    actionLink: HTMLAnchorElement | null
  ): string | null {
    if (!actionLink) {
      return null;
    }

    const href = actionLink.getAttribute('href')?.trim();
    if (!href || /^(?:https?:)?\/\//i.test(href)) {
      return null;
    }

    return href.startsWith('/') ? href : `/${href}`;
  }

  /*
   * Navigate to a target URL, handling both main app and popout window contexts.
   * In a popout window, delegates navigation to the opener via postMessage.
   */
  async navigateToUrl(targetUrl: string): Promise<void> {
    if (this.isPopoutWindow()) {
      this.navigateOpenerWindow(targetUrl);
      return;
    }

    await this.navigateWithRouteReinit(targetUrl);
  }

  private async navigateWithRouteReinit(targetUrl: string): Promise<void> {
    const currentPath = this.stripQueryAndHash(this.router.url);
    const targetPath = this.stripQueryAndHash(targetUrl);

    if (!this.shouldReinitializeDealRoute(currentPath, targetPath)) {
      await this.router.navigateByUrl(targetUrl);
      return;
    }

    const resetUrl = this.getDealRouteParent(targetPath);
    if (!resetUrl) {
      await this.router.navigateByUrl(targetUrl);
      return;
    }

    await this.router.navigateByUrl(resetUrl, { skipLocationChange: true });
    await this.router.navigateByUrl(targetUrl);
  }

  private shouldReinitializeDealRoute(
    currentPath: string,
    targetPath: string
  ): boolean {
    if (!currentPath || currentPath === targetPath) {
      return false;
    }

    return (
      !!this.getDealRouteParent(currentPath) &&
      !!this.getDealRouteParent(targetPath)
    );
  }

  private getDealRouteParent(url: string): string | null {
    const match = url.match(/^(.*)\/deal\/[^/?#]+$/);
    if (!match) {
      return null;
    }

    return match[1] || '/';
  }

  private stripQueryAndHash(url: string): string {
    return url.split(/[?#]/, 1)[0] ?? url;
  }

  private isPopoutWindow(): boolean {
    try {
      return !!window.opener && !window.opener.closed;
    } catch {
      return false;
    }
  }

  private navigateOpenerWindow(targetUrl: string): void {
    try {
      window.opener.postMessage(
        { type: 'ai-chat-navigate', url: targetUrl },
        window.location.origin
      );
    } catch {
      window.open(window.location.origin + targetUrl, '_blank');
    }
  }

  private tryHandleExpandClick(target: Element, event: MouseEvent): boolean {
    const expandButton = target.closest(
      'button.qt-table-expand-btn'
    ) as HTMLButtonElement | null;
    if (!expandButton) {
      return false;
    }

    event.preventDefault();
    this.openExpandedTable(expandButton);
    return true;
  }

  private openExpandedTable(button: HTMLButtonElement): void {
    const block = button.closest<HTMLElement>('.qt-table-block');
    const table = block?.querySelector('table');
    if (!block || !table) {
      return;
    }

    const data: AlloTableDialogData = {
      tableHtml: table.outerHTML,
      title: block.dataset['tableTitle'] ?? '',
      columnCount: Array.from(table.tHead?.rows[0]?.cells ?? []).filter(
        cell => !cell.classList.contains('qt-col--rownum')
      ).length,
      close: () => undefined,
    };

    const ref = this.dialog.open(AiChatTableDialogComponent, {
      data,
      panelClass: 'qt-table-dialog-panel',
      backdropClass: 'qt-table-dialog-backdrop',
      width: '90vw',
      maxWidth: '1040px',
      autoFocus: 'dialog',
    });
    data.close = () => ref.close();
  }

  private exportTableAsCsv(button: HTMLButtonElement): void {
    const container = button.closest('.qt-table-block, .table-wrapper');
    const table = container?.querySelector('table');

    if (!table) {
      return;
    }

    exportTableElementAsCsv(table);
  }
}
